import { AnalyticsContext } from '@carvana/eventable-clickstream';
import {
  MerchTile,
  CoreData,
  FinanceData,
  TileAvailabilityInfo,
  MoreDetailsOptions
} from '@carvana/merch-tile';
import { useCallback, useContext, useEffect, useState } from 'react';
import { assetsCdnUrl, vexCdnUrl } from '~/constants/environment';
import { useConfiguration, useConsumer } from '~/context/common/StaticContext';
import {
  useFavoritesIds,
  useUserLocation,
  useIsLoggedIn
} from '~/context/common/UserContext';
import { useAcquisitionContext } from '~/context/home/AcquisitionContext';
import { useToggleFavorite } from '~/hooks/common/favorites';
import useExperimentBucket from '~/hooks/common/useExperimentBucket';

type OwnProps = {
  availabilityInfo?: TileAvailabilityInfo;
  financeInfo?: FinanceData;
  hasBorder?: boolean;
  hasSoftPull?: boolean;
  handleTileClick?: () => void;
  inVerticalStack?: boolean;
  openModal?: (options: MoreDetailsOptions) => void;
  sectionTitle: string;
  shouldCalculateEvWidth: boolean;
  tradeInApplied?: boolean;
  vehicle: CoreData;
};

export default function VehicleTile({
  availabilityInfo,
  financeInfo,
  hasBorder = true,
  hasSoftPull = false,
  handleTileClick,
  inVerticalStack,
  openModal,
  sectionTitle,
  shouldCalculateEvWidth,
  tradeInApplied = false,
  vehicle
}: OwnProps) {
  const favoriteIds = useFavoritesIds();
  const userLocation = useUserLocation();
  const { showCkPromo = false } = useConfiguration();
  const { trackEvent } = useContext(AnalyticsContext);
  const toggleFavorite = useToggleFavorite();
  const isLoggedIn = useIsLoggedIn();
  const [isFavoritedLocally, setFavoritedLocally] = useState(
    favoriteIds.includes(vehicle.vehicleId)
  );
  const { isFinanceRestricted } = useAcquisitionContext();
  const consumer = useConsumer();

  const handleFavorite = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const { vehicleId, stockNumber } = vehicle;
      const customAttributes = {
        description: `${sectionTitle} heart cta`,
        action: isFavoritedLocally ? 'remove' : 'add',
        vehicleId
      };

      toggleFavorite({ vehicleId, stockNumber, nativeEventSource: 'home' });
      if (isLoggedIn) setFavoritedLocally(v => !v);
      trackEvent('Clicked - Merch Tile Favorite', customAttributes);
    },
    [
      isFavoritedLocally,
      isLoggedIn,
      sectionTitle,
      toggleFavorite,
      trackEvent,
      vehicle
    ]
  );

  useEffect(() => {
    setFavoritedLocally(favoriteIds.includes(vehicle.vehicleId));
  }, [favoriteIds, vehicle.vehicleId]);

  return (
    <MerchTile
      assets={{
        cvnaVexFastlyImageUrl: vexCdnUrl,
        cvnaAppAssetsCdn: assetsCdnUrl
      }}
      availabilityInfo={availabilityInfo}
      configurations={{
        showCkPromo
      }}
      consumer={consumer}
      coreData={vehicle}
      experiments={{
        'merch-favorites-and-saved-searches': useExperimentBucket(
          'merch-favorites-and-saved-searches'
        )
      }}
      financeInfo={financeInfo}
      hasBorder={hasBorder}
      isFavorite={isFavoritedLocally}
      inVerticalStack={inVerticalStack}
      showFinanceInfo={!isFinanceRestricted}
      userInfo={{
        locationState: userLocation.state,
        hasSoftPull,
        tradeInApplied
      }}
      utils={{
        handleFavoriteClick: handleFavorite,
        handleTileClick,
        openMoreDetailsModal: openModal,
        shouldCalculateEvLength: shouldCalculateEvWidth,
        trackEvent
      }}
    />
  );
}
